import React, { useRef, useState, useEffect, ReactNode } from "react";
import "./HorizontalScroll.css";

interface HorizontalScrollProps {
  children: ReactNode[];
  showIndicator?: boolean;
  showArrows?: boolean;
  showArrowsOnDesktop?: boolean;
  indicatorClass?: string;
  dotClass?: string;
  activeDotClass?: string;
  // Keep the row gliding on its own, like the Latest Articles/Blog section
  autoScroll?: boolean;
  autoScrollSpeed?: number; // px per frame
}

// After a swipe, drag or arrow click, wait this long before gliding again
const RESUME_DELAY_MS = 3000;

const HorizontalScroll: React.FC<HorizontalScrollProps> = ({
  children,
  showIndicator = true,
  showArrows = false,
  showArrowsOnDesktop = false,
  indicatorClass = "engage-scroll-indicator",
  dotClass = "engage-scroll-dot",
  activeDotClass = "active",
  autoScroll = false,
  autoScrollSpeed = 0.5,
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const totalCards = React.Children.count(children);
  const cardSelector = ".horizontal-scroll-card";
  const pausedUntil = useRef(0);
  const holding = useRef(false);

  const pauseAuto = () => {
    pausedUntil.current = Date.now() + RESUME_DELAY_MS;
  };

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!autoScroll || !scroller) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    // Track the position as a float: scrollLeft rounds to whole pixels on some
    // screens, so adding 0.5 to it directly would never move
    let pos = scroller.scrollLeft;
    let frame = 0;
    const step = () => {
      const max = scroller.scrollWidth - scroller.clientWidth;
      if (max > 0 && !holding.current && Date.now() >= pausedUntil.current) {
        // Someone scrolled it by hand or with the arrows: carry on from there
        if (Math.abs(scroller.scrollLeft - pos) > 2) pos = scroller.scrollLeft;
        pos = pos >= max ? 0 : pos + autoScrollSpeed;
        scroller.scrollLeft = pos;
      }
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);

    const hold = () => {
      holding.current = true;
    };
    // Listened for on window (the finger may leave the row), so only react to
    // presses that started on this row
    const release = () => {
      if (!holding.current) return;
      holding.current = false;
      pauseAuto();
    };
    scroller.addEventListener("pointerdown", hold);
    scroller.addEventListener("touchstart", hold, { passive: true });
    window.addEventListener("pointerup", release);
    window.addEventListener("touchend", release);
    scroller.addEventListener("wheel", pauseAuto, { passive: true });

    return () => {
      cancelAnimationFrame(frame);
      scroller.removeEventListener("pointerdown", hold);
      scroller.removeEventListener("touchstart", hold);
      window.removeEventListener("pointerup", release);
      window.removeEventListener("touchend", release);
      scroller.removeEventListener("wheel", pauseAuto);
    };
  }, [autoScroll, autoScrollSpeed]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 700);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const onScroll = () => {
      const card = scroller.querySelector(cardSelector);
      if (!card) return;
      const cardWidth = card.clientWidth;
      const idx = Math.round(scroller.scrollLeft / cardWidth);
      setActiveIdx(idx);
    };
    scroller.addEventListener("scroll", onScroll);
    return () => scroller.removeEventListener("scroll", onScroll);
  }, [totalCards]);

  // Improved: Always scroll to the nearest previous/next card based on scroll position
  const scrollToIdx = (idx: number) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    let card = scroller.querySelector(cardSelector) as HTMLElement | null;
    if (!card) return;
    const style = getComputedStyle(card);
    const marginRight = parseFloat(style.marginRight || "0");
    const cardWidth = card.offsetWidth + marginRight;
    // Clamp idx
    idx = Math.max(0, Math.min(idx, totalCards - 1));
    pauseAuto(); // let the smooth scroll finish before gliding again
    scroller.scrollTo({ left: idx * cardWidth, behavior: "smooth" });
    setActiveIdx(idx);
  };

  // On arrow click, calculate the nearest card index
  const handlePrev = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    let card = scroller.querySelector(cardSelector) as HTMLElement | null;
    if (!card) return;
    const style = getComputedStyle(card);
    const marginRight = parseFloat(style.marginRight || "0");
    const cardWidth = card.offsetWidth + marginRight;
    const currentIdx = Math.round(scroller.scrollLeft / cardWidth);
    scrollToIdx(currentIdx - 1);
  };
  const handleNext = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    let card = scroller.querySelector(cardSelector) as HTMLElement | null;
    if (!card) return;
    const style = getComputedStyle(card);
    const marginRight = parseFloat(style.marginRight || "0");
    const cardWidth = card.offsetWidth + marginRight;
    const currentIdx = Math.round(scroller.scrollLeft / cardWidth);
    scrollToIdx(currentIdx + 1);
  };

  const shouldShowArrows = showArrows && (isMobile || showArrowsOnDesktop);

  return (
    <div className="horizontal-scroll-wrapper">
      {shouldShowArrows && (
        <div className="horizontal-scroll-arrows-row">
          <button
            className="horizontal-scroll-arrow prev"
            onClick={handlePrev}
            disabled={activeIdx === 0}
          >
            &#8592;
          </button>
          <button
            className="horizontal-scroll-arrow next"
            onClick={handleNext}
            disabled={activeIdx === totalCards - 1}
          >
            &#8594;
          </button>
        </div>
      )}
      <div
        className={`engage-card-list${autoScroll ? " engage-card-list--auto" : ""}`}
        ref={scrollerRef}
      >
        {children}
      </div>
      {showIndicator && (
        <div className={indicatorClass}>
          {Array.from({ length: totalCards }).map((_, idx) => (
            <span
              key={idx}
              className={
                dotClass + (activeIdx === idx ? ` ${activeDotClass}` : "")
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HorizontalScroll;
